/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { buildCacheKey, getSimpleProducts, ProductInput } from '@msdyn365-commerce-modules/retail-actions';
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings
} from '@msdyn365-commerce/core';
import { getOrderShipmentsHistoryAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CustomersDataActions.g';
import { OrderShipments, SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

export interface IPaging {
    top: number;
    skip: number;
}

export interface IOrderHistory {
    salesOrders: OrderShipments[];
    products: SimpleProduct[];
}

/**
 * Calls the Retail API and returns the products
 * * @param nextPageToken - Next Page Link.
 */
const getOrderShipmentHistory = (paging: IPaging, nextPageToken?: string) => async (ctx: IActionContext): Promise<OrderShipments[]> => {
    const accountNumber: string | undefined = ctx.requestContext.user.customerAccountNumber || '';
    return getOrderShipmentsHistoryAsync(
        {
            callerContext: ctx,
            queryResultSettings: {
                Paging: {
                    Top: paging.top,
                    Skip: paging.skip,
                    NextPageToken: nextPageToken
                }
            }
        },
        accountNumber
    );
};

/**
 * Calls the Retail API and returns the products
 */
const getProducts = (productIds: number[] = [], channelId?: number) => (ctx: IActionContext): Promise<SimpleProduct[]> => {
    const productInputs = productIds.map(productId => new ProductInput(productId, ctx.requestContext.apiSettings, channelId));
    return getSimpleProducts(productInputs, ctx);
};

/**
 *  Action input
 */
export class GetSalesOrderHistoryWithHydrationsInput implements IActionInput {
    public paging: IPaging;
    private apiSettings: ICommerceApiSettings;

    constructor(paging: IPaging, apiSettings: ICommerceApiSettings) {
        this.apiSettings = apiSettings;
        this.paging = paging;
    }

    public getCacheKey = () => buildCacheKey(`OrderHistory`, this.apiSettings);
    /**
     * GetCacheObjectType.
     * @returns String.
     */
    public getCacheObjectType = () => `OrderHistory`;
    public dataCacheType = (): CacheType => 'request';
}

/**
 * Get sales order with hydrations action
 */
export async function getSalesOrderHistoryWithHydrationsAction(
    input: GetSalesOrderHistoryWithHydrationsInput,
    ctx: IActionContext
): Promise<IOrderHistory> {
    if (!ctx) {
        throw new Error(`getSalesOrderWithHydrationsAction - Action context cannot be null/undefined`);
    }
    const channelId = ctx.requestContext.apiSettings.channelId;
    let salesOrders: OrderShipments[] = [];

    try {
        salesOrders = await getOrderShipmentHistory(input.paging)(ctx);
    } catch (error) {
        throw error;
    }

    if (!salesOrders || !salesOrders.length) {
        return {
            salesOrders: [],
            products: []
        };
    }

    const prodIdsByChannel: { [x: number]: number[] } = {
        [channelId]: []
    };

    salesOrders
        .filter(salesOrder => salesOrder)
        .forEach(
            salesOrder =>
                salesOrder &&
                salesOrder.SalesLines &&
                salesOrder.SalesLines.forEach(childrenEntry => {
                    // salesProdIds.push(childrenEntry.ProductId || 0)
                    const prodId = childrenEntry.ProductId || 0;
                    if (!salesOrder.ChannelId) {
                        // Use current channel
                        prodIdsByChannel[channelId].push(prodId);
                    } else {
                        if (!prodIdsByChannel[salesOrder.ChannelId]) {
                            prodIdsByChannel[salesOrder.ChannelId] = [];
                        }
                        prodIdsByChannel[salesOrder.ChannelId].push(prodId);
                    }
                })
        );

    const getProductsPromise = Object.entries(prodIdsByChannel).map(([cId, prodIds]) => getProducts(prodIds, Number(cId))(ctx));

    return Promise.all(getProductsPromise)
        .then(
            (productList): IOrderHistory => {
                const products = productList.reduce((memo, list) => {
                    return [...memo, ...list];
                }, []);

                return {
                    salesOrders,
                    products
                };
            }
        )
        .catch(error => {
            ctx.telemetry.exception(error);
            ctx.telemetry.debug('Failed to get products');
            throw error;
        });
}

export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/order-managment/order-history/get-order-shipment-history',
    action: <IAction<IOrderHistory>>getSalesOrderHistoryWithHydrationsAction
});
